import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  DireccionUnidadNegocio,
  UnidadNegocioRepository
} from 'src/app/domain/service/unidad-negocio/unidad-negocio-repository';
import { UnidadNegocio } from 'src/app/domain/model/agenda/agenda';
import { PageResponse } from 'src/app/domain/model/common/page-response.dto';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP para el repositorio de Unidades de Negocio
 *
 * Implementa el puerto UnidadNegocioRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (UnidadNegocioController en servicios-ms).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto UnidadNegocioRepository del dominio
 * - Trabaja con la entidad de dominio UnidadNegocio
 *
 * Consume de servicios-ms (puerto 8080), no de agenda-ms (puerto 8081)
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpUnidadNegocioRepository implements UnidadNegocioRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/unidades-negocio`;

  /**
   * Página de unidades de negocio activas (keyset/cursor).
   * Endpoint: GET /api/servicios/unidades-negocio?size=N&direction=D[&cursor=C]
   */
  async listarTodas(
    cursor: string | null,
    size: number,
    direction: DireccionUnidadNegocio
  ): Promise<PageResponse<UnidadNegocio>> {
    return firstValueFrom(
      this.http.get<PageResponse<UnidadNegocio>>(this.baseUrl, {
        params: this.paginacion(cursor, size, direction)
      })
    );
  }

  /**
   * Busca unidades de negocio por código y/o nombre (keyset/cursor).
   * Endpoint: GET /api/servicios/unidades-negocio/buscar?codigo=X&nombre=Y&size=N&direction=D[&cursor=C]
   */
  async buscar(
    codigo: string | undefined,
    nombre: string | undefined,
    cursor: string | null,
    size: number,
    direction: DireccionUnidadNegocio
  ): Promise<PageResponse<UnidadNegocio>> {
    let params = this.paginacion(cursor, size, direction);
    if (codigo?.trim()) params = params.set('codigo', codigo.trim());
    if (nombre?.trim()) params = params.set('nombre', nombre.trim());

    return firstValueFrom(
      this.http.get<PageResponse<UnidadNegocio>>(`${this.baseUrl}/buscar`, { params })
    );
  }

  /** size obligatorio y acotado (seguridad-girop §2.3); el cursor se reenvía opaco. */
  private paginacion(
    cursor: string | null,
    size: number,
    direction: DireccionUnidadNegocio
  ): HttpParams {
    validarSize(size);
    let params = new HttpParams()
      .set('size', String(size))
      .set('direction', direction);
    if (cursor) params = params.set('cursor', cursor);
    return params;
  }
}
